// §authenticated-patient handoff (FE PRD 2026-08-08 — consume the ?pt= handoff
// token). A logged-in dashboard patient launching a shop visit arrives with a
// signed, short-lived token `?pt=<jwt>`. We resolve it once at boot to the real
// patient (identity source of truth — the plaintext ?email=/?firstName= params
// are forgeable and used for display only), then:
//   1. pre-fill the basic-contact answer from the resolved patient,
//   2. SKIP the "How can we reach you?" step + its phone/email OTP re-validation
//      (a known patient is never asked to re-verify or re-enter),
//   3. relay the token on submit (create-visit-v2 / lab-authorization / followup)
//      so the BE attaches the visit to the existing account (no "email already
//      exists", correct tenant) — see api.js.
// No ?pt= (anonymous / new patient) → every helper here is inert and the normal
// flow is unchanged. The handoff is best-effort: a resolve failure NEVER blocks
// starting a visit — it just degrades to the normal signup flow.
import { useEffect, useRef } from 'react';
// Relative (not '@/…') so this module is loadable under Jest, whose config has no
// '@' alias mapper — the pure helpers below are unit-tested directly.
import { apiService } from '../services/api';

// sessionStorage keys (same tab lifetime as the tenant key). The token key is
// ALSO read by api.js (inline, to avoid an import cycle) to relay it at submit —
// keep the literal in sync there.
const HANDOFF_TOKEN_STORAGE = 'fitpal_handoff_token';
const HANDOFF_PATIENT_STORAGE = 'fitpal_handoff_patient';
// Snapshot of the plaintext address params from the launch URL (see
// captureHandoffAddress) — survives a refresh, which drops window.location.search.
const HANDOFF_ADDRESS_STORAGE = 'fitpal_handoff_address';

// Module cache so forms read the resolved patient synchronously on first render
// (index.js awaits resolveHandoffAtBoot before rendering when a token is present).
let _resolvedPatient = null;

// Read ?pt= at boot and persist it for the session (survives the multi-step flow
// + refreshes, like the tenant key). Call once, synchronously, before render.
// Returns the token or null.
export function captureHandoffToken() {
  if (typeof window === 'undefined') return null;
  try {
    const pt = new URLSearchParams(window.location.search).get('pt');
    if (pt) {
      sessionStorage.setItem(HANDOFF_TOKEN_STORAGE, pt);
      captureHandoffAddress();
      return pt;
    }
    return sessionStorage.getItem(HANDOFF_TOKEN_STORAGE) || null;
  } catch (e) {
    return null;
  }
}

// Snapshot the dashboard's plaintext address params (?address1=&city=&state=&zip=)
// at boot. These are NOT identity — they're only ever used to PREFILL the shipping
// step for a patient whose token RESOLVED (see handoffAddressAnswer), and the
// patient confirms or edits them before they're submitted. Called from
// captureHandoffToken so it only ever runs on a handoff launch.
function captureHandoffAddress() {
  try {
    const q = new URLSearchParams(window.location.search);
    const snap = {
      address1: (q.get('address1') || q.get('address') || '').trim(),
      address2: (q.get('address2') || '').trim(),
      city: (q.get('city') || '').trim(),
      // The step's <select> is keyed by 2-letter code; a full state name can't be
      // matched, so it degrades to "unselected" and the patient picks it.
      state: (q.get('state') || '').trim().toUpperCase().slice(0, 2),
      zipCode: (q.get('zip') || q.get('zipCode') || '').trim(),
    };
    if (snap.address1 || snap.city || snap.zipCode) {
      sessionStorage.setItem(HANDOFF_ADDRESS_STORAGE, JSON.stringify(snap));
    }
  } catch (e) { /* storage/URL unavailable — prefill is best-effort */ }
}

// The persisted token (present only when this session launched with ?pt=).
export function getHandoffToken() {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(HANDOFF_TOKEN_STORAGE) || null;
  } catch (e) {
    return null;
  }
}

function clearHandoff() {
  _resolvedPatient = null;
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(HANDOFF_TOKEN_STORAGE);
    sessionStorage.removeItem(HANDOFF_PATIENT_STORAGE);
    sessionStorage.removeItem(HANDOFF_ADDRESS_STORAGE);
  } catch (e) { /* storage unavailable */ }
}

// Resolve the token → real patient, ONCE at boot (index.js awaits this before
// first render when a token is present, so getHandoffPatient() is populated on
// the forms' initial render). Best-effort:
//   valid   → cache the patient (identity source of truth) + keep the token.
//   invalid → clear the token so submit never relays a dead token; normal flow.
//   error   → leave the token in place (a transient network failure shouldn't
//             strand a real handoff — the BE re-verifies at submit) but resolve
//             no patient, so the contact step is still shown (safe default).
// Never throws — a resolve failure must not block starting a visit.
export async function resolveHandoffAtBoot() {
  const token = getHandoffToken();
  if (!token) return null;
  try {
    const res = await apiService.resolveIntakeHandoff(token);
    if (res && res.valid && res.patient) {
      _resolvedPatient = res.patient;
      try { sessionStorage.setItem(HANDOFF_PATIENT_STORAGE, JSON.stringify(res.patient)); } catch (e) {}
      return res.patient;
    }
    // Explicitly invalid / expired / forged → don't relay a dead token.
    clearHandoff();
    return null;
  } catch (e) {
    // Transient failure — keep the token for the BE's submit-time re-verify, but
    // no trusted patient to prefill → the patient completes the normal step.
    console.warn('[handoff] resolve failed (degrading to normal flow):', e && e.message);
    return null;
  }
}

// The resolved (trusted) patient, or null. Reads the module cache, falling back
// to the sessionStorage snapshot (e.g. a mid-session route remount).
export function getHandoffPatient() {
  if (_resolvedPatient) return _resolvedPatient;
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(HANDOFF_PATIENT_STORAGE);
    if (!raw) return null;
    _resolvedPatient = JSON.parse(raw);
    return _resolvedPatient;
  } catch (e) {
    return null;
  }
}

// True when a valid handoff resolved to a real patient (drives seed + the
// email-exists bypass — a resolved patient is known, so re-entry must never be
// blocked by the "email already exists" gate).
export function hasResolvedHandoff() {
  return !!getHandoffPatient();
}

// True for ANY live handoff launch — a token is present and has NOT been proven
// invalid (resolveHandoffAtBoot clears the token on an explicit valid:false, so a
// forged/expired token lands back on the normal anonymous flow).
//
// This is deliberately weaker than hasResolvedHandoff() and is used ONLY for the
// "email already exists" gate. If resolve fails transiently (endpoint down, slow,
// boot-timeout), we have no patient — and gating on hasResolvedHandoff() would
// then hard-block a real logged-in patient on their own email with no way past
// the step. The gate is a courtesy check for new signups, not a security control:
// the BE re-verifies the token and attaches the visit server-side at submit. So
// under a handoff we let them through and let the BE decide.
export function underHandoff() {
  return !!getHandoffToken();
}

// Normalize a BE dob (ISO 'YYYY-MM-DD[...]' or already MM/DD/YYYY) to the
// MM/DD/YYYY the contact answer / submit expect. Unknown shapes pass through.
function normalizeDob(dob) {
  if (!dob || typeof dob !== 'string') return '';
  const iso = dob.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  return dob;
}

// Build the seeded `basic-contact` answer from the resolved patient (or null).
// Shape matches what BasicContactStep emits + what submit/checkout read
// (name, email, phone, dob) plus patientId for any downstream use. No
// phoneVerifyToken — the OTP step is skipped; the BE trusts the handoff token.
export function handoffContactAnswer() {
  const p = getHandoffPatient();
  if (!p) return null;
  const name = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
  return {
    name,
    email: p.email || '',
    phone: p.phone || '',
    dob: normalizeDob(p.dob),
    patientId: p.id,
    fromHandoff: true,
  };
}

// Build the seeded `shipping-address` answer for a resolved handoff patient, so a
// known patient CONFIRMS an address instead of retyping one (PRD 2026-08-11 §4.4).
// Source order:
//   1. the resolved patient record (trusted — server-side, from the token), then
//   2. the launch URL's plaintext address params (captureHandoffAddress).
// (2) is only ever consulted when a token RESOLVED, so an anonymous visitor can
// never be handed a prefilled address by a crafted URL. Even then it's just a
// prefill the patient sees and confirms — never an identity claim, and never
// submitted without passing through the step.
// Returns null when there's no handoff or no usable address on either source.
export function handoffAddressAnswer() {
  const p = getHandoffPatient();
  if (!p) return null;
  let snap = null;
  if (typeof window !== 'undefined') {
    try { snap = JSON.parse(sessionStorage.getItem(HANDOFF_ADDRESS_STORAGE) || 'null'); } catch (e) { snap = null; }
  }
  const pick = (...vals) => {
    for (const v of vals) if (typeof v === 'string' && v.trim()) return v.trim();
    return '';
  };
  const state = pick(p.state, snap && snap.state).toUpperCase().slice(0, 2);
  const answer = {
    address1: pick(p.address1, p.address, snap && snap.address1),
    address2: pick(p.address2, snap && snap.address2),
    city: pick(p.city, snap && snap.city),
    state,
    zipCode: pick(p.zipCode, p.zip, snap && snap.zipCode),
    fromHandoff: true,
  };
  // Nothing usable → no seed (the patient gets the normal empty form).
  if (!answer.address1 && !answer.city && !answer.zipCode) return null;
  return answer;
}

// A resolved patient is "complete" only when EVERY field the intake submits as a
// required contact value is present: first + last name, email, phone, and a
// resolvable dob. This gates the skip: if the dashboard record is missing any of
// these (dob especially — it's never in the URL, only from resolve; or a
// last-name-less record), skipping the contact step would submit an empty field
// and Beluga rejects the whole visit with "Missing values" AFTER the card is
// charged. Incomplete → we still PREFILL, but keep the step so the patient fills
// the gap.
export function handoffPatientComplete() {
  const p = getHandoffPatient();
  if (!p) return false;
  return !!(p.firstName && p.lastName && p.email && p.phone && normalizeDob(p.dob));
}

// Remove the shared basic-contact step from a built flow ONLY for a COMPLETE
// handoff patient (see handoffPatientComplete). A partial handoff keeps the step
// (prefilled via useHandoffSeed) so no required contact field is ever left empty.
// No-op without a handoff, and for flows that have no basic-contact step.
export function skipHandoffContact(flow) {
  if (!Array.isArray(flow) || !handoffPatientComplete()) return flow;
  return flow.filter((step) => step && step.type !== 'basic-contact');
}

// Seed answers['basic-contact'] (and, when we know it, answers['shipping-address'])
// from the resolved patient, once, if not already present. Call unconditionally at
// the top of each form (React hook). Inert when there's no handoff. Pairs with
// skipHandoffContact so the (removed) contact step's data is still available to
// submit + checkout; the address step is never removed — it renders as a
// confirm-this-address step (ShippingAddressStep reads the `fromHandoff` flag).
//
// How the seed interacts with RESTORED saved progress (forms rehydrate answers
// from localStorage), which is what makes this non-trivial:
//   • COMPLETE handoff → the contact step is REMOVED, so the patient can never
//     correct what's submitted. The trusted record therefore WINS outright:
//     otherwise a stale/abandoned intake left in this browser (possibly another
//     person's email + dob) would be submitted for a token-identified patient.
//   • INCOMPLETE handoff → the step is SHOWN. Fill only the fields the saved
//     answer left empty, so a value the patient just typed (the missing dob, a
//     corrected phone, an OTP token) is never clobbered by a refresh.
// The address is only ever a prefill for a step the patient always sees, so it
// merges the same way and never overrides what's already there.
function mergeMissing(saved, seed) {
  const out = { ...seed, ...saved };
  Object.keys(seed).forEach((k) => {
    const cur = out[k];
    if (cur === undefined || cur === null || cur === '') out[k] = seed[k];
  });
  return out;
}

export function useHandoffSeed(setAnswers) {
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    const contact = handoffContactAnswer();
    const address = handoffAddressAnswer();
    if (!contact && !address) return;
    seeded.current = true;
    const contactOverrides = handoffPatientComplete();
    setAnswers((prev) => {
      const next = { ...prev };
      if (contact) {
        const saved = prev && prev['basic-contact'];
        next['basic-contact'] = !saved || contactOverrides ? contact : mergeMissing(saved, contact);
      }
      if (address) {
        const saved = prev && prev['shipping-address'];
        next['shipping-address'] = saved ? mergeMissing(saved, address) : address;
      }
      return next;
    });
  }, [setAnswers]);
}

// §advanced-peptide session (dashboard FE ask, 2026-09-28). The submit response
// carries { intakeId, threadId }; keep them for the rest of THIS tab's session
// so the completion screen (and an embedded dashboard reading the same
// storage) can reach the patient's intake/thread without the email deep link.
// sessionStorage, not localStorage: this is one visit's context, not identity.
// Note for the dashboard: sessionStorage is per-origin — a standalone intake on
// intake.fitpal.com can't be read from dashboard.fitpal.com, which is why the
// same ids also ride the fitpal:visit-complete postMessage (embedBridge) and
// the completion screen's dashboard link (?intakeId=&threadId=).
const ADVANCED_PEPTIDE_SESSION_STORAGE = 'fitpal_advanced_peptide_session';

export function setAdvancedPeptideSession({ intakeId, threadId } = {}) {
  if (typeof window === 'undefined' || !intakeId) return null;
  const session = { intakeId: String(intakeId), threadId: threadId ? String(threadId) : null, savedAt: new Date().toISOString() };
  try { sessionStorage.setItem(ADVANCED_PEPTIDE_SESSION_STORAGE, JSON.stringify(session)); } catch (e) { /* storage unavailable */ }
  return session;
}

export function getAdvancedPeptideSession() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(ADVANCED_PEPTIDE_SESSION_STORAGE);
    if (!raw) return null;
    const s = JSON.parse(raw);
    return s && s.intakeId ? s : null;
  } catch (e) {
    return null;
  }
}

export function clearAdvancedPeptideSession() {
  if (typeof window === 'undefined') return;
  try { sessionStorage.removeItem(ADVANCED_PEPTIDE_SESSION_STORAGE); } catch (e) { /* storage unavailable */ }
}
